import { supabase } from '../lib/supabase';
import { Question } from '../types';

export const questionsService = {
  // Garder une trace des questions déjà affichées
  seenQuestions: new Set<string>(),
  allQuestions: [] as Question[],

  getAll: async (): Promise<Question[]> => {
    try {
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching questions:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des questions');
    }
  },

  getRandomExcept: async (excludeId?: string): Promise<Question | null> => {
    try {
      // Si nous n'avons pas encore chargé toutes les questions
      if (questionsService.allQuestions.length === 0) {
        const { data, error } = await supabase.from('questions').select('*');
        if (error) throw error;
        if (!data || data.length === 0) return null;
        questionsService.allQuestions = data;
      }

      // Si toutes les questions ont été vues, réinitialiser
      if (questionsService.seenQuestions.size >= questionsService.allQuestions.length) {
        questionsService.seenQuestions.clear();
        // Exclure la dernière question vue pour éviter la répétition
        if (excludeId) {
          questionsService.seenQuestions.add(excludeId);
        }
      }

      // Filtrer les questions non vues
      const availableQuestions = questionsService.allQuestions.filter(
        q => !questionsService.seenQuestions.has(q.id)
      );

      if (availableQuestions.length === 0) return null;

      // Sélectionner une question aléatoire parmi les disponibles
      const randomIndex = Math.floor(Math.random() * availableQuestions.length);
      const selectedQuestion = availableQuestions[randomIndex];

      // Marquer la question comme vue
      questionsService.seenQuestions.add(selectedQuestion.id);

      return selectedQuestion;
    } catch (error: any) {
      console.error('Error fetching random question:', error);
      throw new Error(error?.message || 'Erreur lors du chargement de la question aléatoire');
    }
  },

  getRandom: async (): Promise<Question | null> => {
    return questionsService.getRandomExcept();
  },

  add: async (content: string): Promise<Question> => {
    if (!content.trim()) {
      throw new Error('Le contenu de la question ne peut pas être vide');
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Vous devez être connecté pour ajouter une question');
      }

      const { data, error } = await supabase
        .from('questions')
        .insert([{ 
          content: content.trim(),
          user_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('Erreur lors de la création de la question');

      // Réinitialiser le cache des questions
      questionsService.allQuestions = [];
      questionsService.seenQuestions.clear();

      return data;
    } catch (error: any) {
      console.error('Error adding question:', error);
      throw new Error(error?.message || 'Erreur lors de l\'ajout de la question');
    }
  },

  delete: async (id: string): Promise<void> => {
    if (!id) {
      throw new Error('ID de question invalide');
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Vous devez être connecté pour supprimer une question');
      }

      const { error } = await supabase
        .from('questions')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Réinitialiser le cache des questions
      questionsService.allQuestions = [];
      questionsService.seenQuestions.clear();
    } catch (error: any) {
      console.error('Error deleting question:', error);
      throw new Error(error?.message || 'Erreur lors de la suppression de la question');
    }
  },

  deleteAll: async (): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Vous devez être connecté pour supprimer les questions');
      }

      const { error } = await supabase
        .from('questions')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      // Réinitialiser le cache des questions
      questionsService.allQuestions = [];
      questionsService.seenQuestions.clear();
    } catch (error: any) {
      console.error('Error deleting all questions:', error);
      throw new Error(error?.message || 'Erreur lors de la suppression des questions');
    }
  }
};